import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, User, LogOut, BarChart3, BookOpen, Wallet , Gift } from 'lucide-react';
import { useState } from 'react';
import { useAuthStore } from '../../store/authStore';
import { motion, AnimatePresence } from 'framer-motion';
import LanguageSelector from '../LanguageSelector';
import WalletButton from '../WalletButton';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { user, isAuthenticated, logout } = useAuthStore();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getTierBadge = (tier: string) => {
    switch (tier) {
      case 'pro': return 'bg-steel-blue text-white';
      case 'premium': return 'bg-gradient-cta text-white';
      case 'enterprise': return 'bg-cream text-navy-dark';
      default: return 'bg-sky-blue/20 text-sky-blue';
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-navy-dark/95 backdrop-blur-lg border-b border-sky-blue/20">
      <nav className="max-w-full mx-auto px-2 sm:px-3 lg:px-4">
        <div className="flex items-center justify-between h-16">

          <Link to="/" className="flex items-center space-x-1.5 group flex-shrink-0 mr-0">
            <img src="/rv-icon.svg" alt="RV" className="w-8 h-8 group-hover:drop-shadow-[0_0_10px_rgba(0,217,255,0.5)] transition-all" />
            <span className="text-base font-bold gradient-text-neon hidden sm:inline whitespace-nowrap">
              Residual Vault
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center space-x-3">
            <Link to="/staking" className="text-cream hover:text-neon-red font-medium transition-colors text-sm">
              Staking
            </Link>
            <Link to="/intelligence" className="text-cream hover:text-neon-red font-medium transition-colors text-sm">
              Intelligence
            </Link>
              <Link to="/risk" className="text-cream hover:text-sky-blue transition-colors font-medium">Safety Scores</Link>
            <Link to="/calculator" className="text-cream hover:text-neon-red font-medium transition-colors text-sm">
              Calculator
            </Link>
            <Link to="/leaderboard" className="text-cream hover:text-neon-red font-medium transition-colors text-sm">
              🏆 Leaderboard
            </Link>
            <Link to="/blog" className="text-cream hover:text-neon-red font-medium transition-colors text-sm">
              Blog
            </Link>
            <Link to="/education" className="text-cream hover:text-neon-red font-medium transition-colors text-sm">
              Education
            </Link>
            <Link to="/store" className="text-cream hover:text-neon-red font-medium transition-colors text-sm">
              Store
            </Link>
            <Link to="/pricing" className="text-cream hover:text-neon-red font-medium transition-colors text-sm">
              Pricing
            </Link>
          </div>

          <div className="hidden lg:flex items-center space-x-3">
            <LanguageSelector />
            <WalletButton />

            {isAuthenticated ? (
              <>
                <Link to="/dashboard" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-steel-blue/20 text-sky-blue hover:bg-steel-blue/30 transition-all">
                  <BarChart3 className="w-4 h-4" />
                  <span className="hidden lg:inline">Dashboard</span>
                </Link>

                <div className="relative">
                  <button onClick={() => setUserMenuOpen(!userMenuOpen)} className="flex items-center space-x-2 p-2 rounded-lg hover:bg-steel-blue/20 transition-all">
                    <div className="w-9 h-9 bg-gradient-cta rounded-full flex items-center justify-center shadow-neon">
                      <User className="w-5 h-5 text-white" />
                    </div>
                    <div className="text-left hidden lg:block">
                      <p className="text-sm font-medium text-cream">{user?.firstName || 'User'}</p>
                      <p className={`text-xs px-2 rounded-full inline-block ${getTierBadge(user?.subscriptionTier || 'free')}`}>
                        {user?.subscriptionTier || 'free'}
                      </p>
                    </div>
                  </button>

                  <AnimatePresence>
                    {userMenuOpen && (
                      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="absolute right-0 mt-2 w-48 bg-navy-dark border border-sky-blue/30 rounded-lg shadow-card-hover py-1">
                        <Link to="/portfolio" className="flex items-center px-4 py-2 text-sm text-cream hover:bg-steel-blue/20" onClick={() => setUserMenuOpen(false)}>
                          <Wallet className="w-4 h-4 mr-2 text-sky-blue" />
                          Portfolio
                        </Link>
                        <Link to="/education" className="flex items-center px-4 py-2 text-sm text-cream hover:bg-steel-blue/20" onClick={() => setUserMenuOpen(false)}>
                          <BookOpen className="w-4 h-4 mr-2 text-sky-blue" />
                          My Learning
                        </Link>
                        <Link to="/referral" className="flex items-center px-4 py-2 text-sm text-cream hover:bg-steel-blue/20" onClick={() => setUserMenuOpen(false)}>
                          <Gift className="w-4 h-4 mr-2 text-sky-blue" />
                          Referral Program
                        </Link>
                        <Link to="/profile" className="flex items-center px-4 py-2 text-sm text-cream hover:bg-steel-blue/20" onClick={() => setUserMenuOpen(false)}>
                          <User className="w-4 h-4 mr-2 text-sky-blue" />
                          Profile
                        </Link>
                        <hr className="my-1 border-sky-blue/20" />
                        <button onClick={() => { setUserMenuOpen(false); handleLogout(); }} className="flex items-center w-full px-4 py-2 text-sm text-neon-red hover:bg-neon-red/10">
                          <LogOut className="w-4 h-4 mr-2" />
                          Logout
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </>
            ) : (
              <>
                <Link to="/login" className="text-cream hover:text-neon-red font-medium transition-colors text-sm">
                  Sign In
                </Link>
                <Link to="/register" className="btn-neon">
                  Get Started
                </Link>
              </>
            )}
          </div>

          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden p-2 rounded-lg hover:bg-steel-blue/20 text-cream">
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="lg:hidden py-4 border-t border-sky-blue/20">
              <div className="flex flex-col space-y-2">
                <Link to="/staking" className="px-4 py-2 text-cream hover:bg-steel-blue/20 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Staking Options</Link>
                <Link to="/intelligence" className="px-4 py-2 text-cream hover:bg-steel-blue/20 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Intelligence</Link>
                <Link to="/risk" className="px-4 py-2 text-cream hover:bg-steel-blue/20 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Safety Scores</Link>
                <Link to="/calculator" className="px-4 py-2 text-cream hover:bg-steel-blue/20 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Calculator</Link>
                <Link to="/leaderboard" className="px-4 py-2 text-cream hover:bg-steel-blue/20 rounded-lg" onClick={() => setMobileMenuOpen(false)}>🏆 Leaderboard</Link>
                <Link to="/blog" className="px-4 py-2 text-cream hover:bg-steel-blue/20 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Blog</Link>
                <Link to="/education" className="px-4 py-2 text-cream hover:bg-steel-blue/20 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Education</Link>
                <Link to="/store" className="px-4 py-2 text-cream hover:bg-steel-blue/20 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Store</Link>
                <Link to="/pricing" className="px-4 py-2 text-cream hover:bg-steel-blue/20 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Pricing</Link>

                {isAuthenticated ? (
                  <>
                    <hr className="my-2 border-sky-blue/20" />
                    <Link to="/dashboard" className="px-4 py-2 text-cream hover:bg-steel-blue/20 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Dashboard</Link>
                    <Link to="/portfolio" className="px-4 py-2 text-cream hover:bg-steel-blue/20 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Portfolio</Link>
                    <Link to="/referral" className="px-4 py-2 text-cream hover:bg-steel-blue/20 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Referral Program</Link>
                    <Link to="/profile" className="px-4 py-2 text-cream hover:bg-steel-blue/20 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Profile</Link>
                    <button onClick={() => { setMobileMenuOpen(false); handleLogout(); }} className="text-left px-4 py-2 text-neon-red hover:bg-neon-red/10 rounded-lg">Logout</button>
                  </>
                ) : (
                  <>
                    <hr className="my-2 border-sky-blue/20" />
                    <Link to="/login" className="px-4 py-2 text-cream hover:bg-steel-blue/20 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Sign In</Link>
                    <Link to="/register" className="mx-4 py-2 bg-gradient-cta text-white rounded-lg text-center font-semibold hover:shadow-neon transition-all" onClick={() => setMobileMenuOpen(false)}>Get Started</Link>
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
}